import { Box, useSafeLayoutEffect } from '@hover/blueprint';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';

import { BuildingLoadingOverlay } from 'src/modules/homeownerApp/components/ThreeDModel/BuildingLoadingOverlay';
import type { JobThreeDFiles } from 'src/shared/graphql/generated';
import { useEffectOnMount } from 'src/shared/hooks';

import type { FocusElement } from './machete';
import {
  getModelData,
  highlightFeature,
  labelBuilding,
  renderMachete,
} from './machete';
import type { MacheteType } from './types';

export interface ThreeDModelProps {
  threeDFiles: JobThreeDFiles;
  focusElement?: FocusElement;
}

export const ThreeDModel = ({
  threeDFiles,
  focusElement,
}: ThreeDModelProps) => {
  const canvasRef = useRef(null);
  const [modelData, setModelData] = useState<{
    geometry: object | null;
    metadata: object | null;
  } | null>(null);
  const [building, setBuilding] = useState<MacheteType | null>(null);
  const [viewer, setViewer] = useState<object | null>(null);
  const [isBuildingReady, setIsBuildingReady] = useState<boolean>(false);
  const [showFailedToLoad, setShowFailedToLoad] = useState<boolean>(false);

  useEffect(() => {
    if (focusElement) {
      if (focusElement && building && viewer) {
        void highlightFeature(building, viewer, focusElement);
      }
    }
  }, [focusElement, building, viewer]);

  useEffectOnMount(() => {
    let data;
    const getData = async () => {
      data = await getModelData(threeDFiles);
      if (data?.geometry && data?.metadata) {
        setModelData(data);
      } else {
        setShowFailedToLoad(true);
      }
    };

    void getData();
  });

  useEffect(() => {
    if (modelData?.geometry && modelData?.metadata) {
      const { geometry, metadata } = modelData;
      void renderMachete({ canvasRef, geometry, metadata }).then(
        macheteStuff => {
          if (macheteStuff?.building) {
            setBuilding(macheteStuff.building as MacheteType);
            setIsBuildingReady(true);
          }
          if (macheteStuff?.viewer) {
            setViewer(macheteStuff.viewer);
          }
        },
      );
    }
  }, [modelData, modelData?.geometry, modelData?.metadata]);

  useSafeLayoutEffect(() => {
    if (!isBuildingReady) return;
    labelBuilding(building);
  }, [building, isBuildingReady]);

  return (
    <Box
      alignSelf="center"
      borderRadius="400"
      flexDirection="column"
      height="100%"
      overflow="hidden"
      position="relative"
      width="100%"
    >
      <Box
        _hover={{ cursor: 'grab' }}
        flex={1}
        ref={canvasRef}
        style={{ aspectRatio: '16/9' }}
        width={1}
      />
      <AnimatePresence initial={false} mode="popLayout">
        {!isBuildingReady && (
          <BuildingLoadingOverlay hasError={showFailedToLoad} />
        )}
      </AnimatePresence>
    </Box>
  );
};
